import { useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { Combobox } from '@/components/ui/combobox';

// Editable review table for PDF statement imports, shown between parse and
// commit. Rows are the exact CSV-shape objects the import pipeline consumes,
// so every edit flows straight into the unchanged CSV commit path.
//
// Date/Amount/Type come from the deterministic parser — they stay editable
// (the spec requires it) but are visually marked as parsed-from-statement.

interface CsvRow {
  Date: string;
  Description: string;
  Category: string;
  Amount: string;
  Type: string;
  Account: string;
  Payee: string;
  Tags: string;
  Notes: string;
}

interface StatementReviewTableProps {
  rows: CsvRow[];
  onRowsChange: (rows: CsvRow[]) => void;
  categoryItems: { value: string; label: string }[];
  currency: string;
  /** Called when the user assigns a category to rows of a payee. */
  onCategoryCorrected: (payee: string, categoryName: string) => void;
  rememberCorrections: boolean;
  onRememberChange: (value: boolean) => void;
}

const needsReview = (row: CsvRow) =>
  !row.Category || row.Tags.split(',').some((t) => t.trim() === 'review');

export default function StatementReviewTable({
  rows,
  onRowsChange,
  categoryItems,
  currency,
  onCategoryCorrected,
  rememberCorrections,
  onRememberChange,
}: StatementReviewTableProps) {
  // Freeze the display order on first render: needs-attention rows first.
  // Re-sorting live would make rows jump away while the user edits them.
  const [order] = useState<number[]>(() => {
    const indices = rows.map((_, i) => i);
    indices.sort((a, b) => Number(needsReview(rows[b])) - Number(needsReview(rows[a])));
    return indices;
  });
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [pendingBulk, setPendingBulk] = useState<{
    payee: string;
    category: string;
    count: number;
  } | null>(null);

  const updateRow = (index: number, patch: Partial<CsvRow>) => {
    const next = rows.slice();
    next[index] = { ...next[index], ...patch };
    onRowsChange(next);
  };

  const handleCategoryChange = (index: number, categoryName: string) => {
    updateRow(index, { Category: categoryName });
    const payee = rows[index].Payee?.trim();
    if (!payee) return;
    onCategoryCorrected(payee, categoryName);
    const others = rows.filter(
      (r, i) => i !== index && r.Payee?.trim().toLowerCase() === payee.toLowerCase() && r.Category !== categoryName
    ).length;
    if (others > 0) setPendingBulk({ payee, category: categoryName, count: others });
  };

  const applyBulk = () => {
    if (!pendingBulk) return;
    const next = rows.map((r) =>
      r.Payee?.trim().toLowerCase() === pendingBulk.payee.toLowerCase()
        ? { ...r, Category: pendingBulk.category }
        : r
    );
    onRowsChange(next);
    setPendingBulk(null);
  };

  const summary = useMemo(() => {
    let moneyIn = 0;
    let moneyOut = 0;
    const byCategory = new Map<string, number>();
    for (const row of rows) {
      const amount = parseFloat(row.Amount);
      if (isNaN(amount)) continue;
      if (amount >= 0) moneyIn += amount;
      else moneyOut += -amount;
      const key = row.Category || 'Uncategorized';
      byCategory.set(key, (byCategory.get(key) ?? 0) + Math.abs(amount));
    }
    const topCategories = [...byCategory.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6);
    const reviewCount = rows.filter(needsReview).length;
    return { moneyIn, moneyOut, topCategories, reviewCount };
  }, [rows]);

  const fmt = (n: number) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const parsedCellClass =
    'bg-[#F3F4F6] dark:bg-[#1E2B42] text-[#1C1C1C] dark:text-[#E8E8E8] rounded-md border border-transparent focus:border-[#439476] outline-none px-1.5 py-1 text-xs w-full';
  const editCellClass =
    'bg-transparent text-[#1C1C1C] dark:text-[#E8E8E8] rounded-md border border-[#2F2F2F]/10 dark:border-[#333840] focus:border-[#439476] outline-none px-1.5 py-1 text-xs w-full';

  return (
    <div className="space-y-3">
      {/* Summary strip */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#2F2F2F] dark:text-[#A3A3A3]">
        <span className="font-medium text-[#1C1C1C] dark:text-[#E8E8E8]">
          {rows.length} transactions
        </span>
        <span className="text-red-600 dark:text-red-400">−{fmt(summary.moneyOut)} {currency}</span>
        <span className="text-green-700 dark:text-green-400">+{fmt(summary.moneyIn)} {currency}</span>
        {summary.reviewCount > 0 && (
          <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
            <AlertTriangle className="w-3.5 h-3.5" />
            {summary.reviewCount} need review (shown first)
          </span>
        )}
      </div>

      {/* Bulk apply prompt */}
      {pendingBulk && (
        <div className="flex items-center justify-between gap-3 p-2.5 bg-[rgba(8,31,61,0.06)] dark:bg-[rgba(8,31,61,0.25)] rounded-lg text-xs">
          <span className="text-[#1C1C1C] dark:text-[#E8E8E8]">
            Apply <b>{pendingBulk.category}</b> to {pendingBulk.count} more{' '}
            {pendingBulk.count === 1 ? 'transaction' : 'transactions'} from <b>{pendingBulk.payee}</b>?
          </span>
          <span className="flex gap-2 shrink-0">
            <button
              onClick={applyBulk}
              className="px-2.5 py-1 rounded-md bg-[#439476] text-white hover:opacity-90"
            >
              Apply to all
            </button>
            <button
              onClick={() => setPendingBulk(null)}
              className="px-2.5 py-1 rounded-md border border-[#2F2F2F]/20 dark:border-[#333840] text-[#2F2F2F] dark:text-[#A3A3A3]"
            >
              No
            </button>
          </span>
        </div>
      )}

      {/* Table */}
      <div className="max-h-[420px] overflow-auto rounded-xl border border-[#2F2F2F]/10 dark:border-[#333840]">
        <table className="w-full text-xs border-collapse min-w-[900px]">
          <thead className="sticky top-0 z-10 bg-[#F9FAFB] dark:bg-[#141F33]">
            <tr className="text-left text-[#2F2F2F] dark:text-[#A3A3A3]">
              <th className="p-2 w-6"></th>
              <th className="p-2 w-24" title="Parsed from statement">Date *</th>
              <th className="p-2 w-40">Payee</th>
              <th className="p-2">Description</th>
              <th className="p-2 w-48">Category</th>
              <th className="p-2 w-24 text-right" title="Parsed from statement">Amount *</th>
              <th className="p-2 w-24" title="Parsed from statement">Type *</th>
              <th className="p-2 w-32">Tags</th>
            </tr>
          </thead>
          <tbody>
            {order.map((idx) => {
              const row = rows[idx];
              const attention = needsReview(row);
              const amount = parseFloat(row.Amount);
              return (
                <>
                  <tr
                    key={idx}
                    className={`border-t border-[#2F2F2F]/5 dark:border-[#333840]/50 align-top ${
                      attention ? 'bg-amber-50/60 dark:bg-amber-900/10' : ''
                    }`}
                  >
                    <td className="p-1.5 pt-2.5">
                      <button
                        onClick={() =>
                          setExpanded((prev) => {
                            const next = new Set(prev);
                            next.has(idx) ? next.delete(idx) : next.add(idx);
                            return next;
                          })
                        }
                        className="text-[#2F2F2F]/60 dark:text-[#A3A3A3]/60 hover:text-[#439476]"
                        title="Show notes"
                      >
                        {expanded.has(idx) ? (
                          <ChevronDown className="w-3.5 h-3.5" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </td>
                    <td className="p-1.5">
                      <input
                        value={row.Date}
                        onChange={(e) => updateRow(idx, { Date: e.target.value })}
                        className={parsedCellClass}
                        title="Parsed from statement"
                      />
                    </td>
                    <td className="p-1.5">
                      <input
                        value={row.Payee}
                        onChange={(e) => updateRow(idx, { Payee: e.target.value })}
                        className={editCellClass}
                      />
                    </td>
                    <td className="p-1.5">
                      <input
                        value={row.Description}
                        onChange={(e) => updateRow(idx, { Description: e.target.value })}
                        className={editCellClass}
                      />
                    </td>
                    <td className="p-1.5">
                      <Combobox
                        items={categoryItems}
                        value={row.Category}
                        onChange={(value) => handleCategoryChange(idx, value)}
                        placeholder="Uncategorized"
                        className={row.Category ? '' : 'border-amber-400 dark:border-amber-600'}
                        contentStyle={{ zIndex: 9999 }}
                      />
                    </td>
                    <td className="p-1.5">
                      <input
                        value={row.Amount}
                        onChange={(e) => updateRow(idx, { Amount: e.target.value })}
                        className={`${parsedCellClass} text-right ${
                          !isNaN(amount) && amount < 0
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-green-700 dark:text-green-400'
                        }`}
                        title="Parsed from statement"
                      />
                    </td>
                    <td className="p-1.5">
                      <select
                        value={row.Type}
                        onChange={(e) => updateRow(idx, { Type: e.target.value })}
                        className={parsedCellClass}
                        title="Parsed from statement"
                      >
                        <option value="expense">expense</option>
                        <option value="income">income</option>
                        <option value="transfer">transfer</option>
                      </select>
                    </td>
                    <td className="p-1.5">
                      <input
                        value={row.Tags}
                        onChange={(e) => updateRow(idx, { Tags: e.target.value })}
                        className={editCellClass}
                        placeholder="comma, separated"
                      />
                    </td>
                  </tr>
                  {expanded.has(idx) && (
                    <tr key={`${idx}-notes`} className="bg-[#F9FAFB]/60 dark:bg-[#141F33]/60">
                      <td></td>
                      <td colSpan={7} className="p-1.5 pb-2.5">
                        <textarea
                          value={row.Notes}
                          onChange={(e) => updateRow(idx, { Notes: e.target.value })}
                          rows={2}
                          className={`${editCellClass} resize-y`}
                          placeholder="Notes"
                        />
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Category totals */}
      {summary.topCategories.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {summary.topCategories.map(([name, total]) => (
            <span
              key={name}
              className={`px-2 py-1 rounded-md text-[11px] border ${
                name === 'Uncategorized'
                  ? 'border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400'
                  : 'border-[#2F2F2F]/10 dark:border-[#333840] text-[#2F2F2F] dark:text-[#A3A3A3]'
              }`}
            >
              {name}: {fmt(total)} {currency}
            </span>
          ))}
        </div>
      )}

      {/* Remember corrections */}
      <label className="flex items-center gap-2 text-xs text-[#2F2F2F] dark:text-[#A3A3A3] cursor-pointer">
        <input
          type="checkbox"
          checked={rememberCorrections}
          onChange={(e) => onRememberChange(e.target.checked)}
          className="rounded"
        />
        Remember my category corrections for future imports
      </label>
    </div>
  );
}
